import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

export const DeleteAccount = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      console.log("Starting account deletion process");
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("No authenticated user found");
      }
      
      const { error: deleteError } = await supabase.functions.invoke('delete-user');
      
      if (deleteError) {
        console.error("Error from delete-user function:", deleteError);
        throw deleteError;
      }

      console.log("Account deleted successfully");
      setIsDialogOpen(false);
      
      toast({
        title: "Account Deleted",
        description: "Your account has been permanently deleted.",
      });
      
      // Sign out and redirect
      await supabase.auth.signOut();
      navigate("/auth/signin");
    } catch (error: any) {
      console.error("Error deleting account:", error);
      setIsDialogOpen(false);
      
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete account. Please try again or contact support.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="mt-6 border-t pt-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Delete Account</h2>
      <p className="text-sm text-gray-600 mb-4">
        Once you delete your account, there is no going back. This action is permanent and will remove all your data.
      </p>
      
      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogTrigger asChild>
          <Button variant="destructive">
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Account
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your account and remove all your data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete Account"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};